<template>
  <div :data-testid="dataTestid" class="h-64 w-full">
    <VChart :option="option" :theme="OMR_ECHARTS_THEME" autoresize :data-testid="`${dataTestid}-chart`" class="h-full w-full" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'
import { makeCompact } from '@/helpers/chartFormat'

const props = withDefaults(
  defineProps<{
    categories: string[]
    values: (number | null)[]
    color: string
    highlightColor?: string
    highlightIndex?: number
    locale: string
    formatValue?: (value: number) => string
    gridLines?: boolean
    capOutliers?: boolean
    marker?: number | null
    markerLabel?: string
    markerColor?: string
    dataTestid?: string
  }>(),
  {
    highlightColor: undefined,
    highlightIndex: undefined,
    formatValue: undefined,
    gridLines: false,
    capOutliers: false,
    marker: null,
    markerLabel: undefined,
    markerColor: undefined,
    dataTestid: 'insights-benchmark-chart',
  },
)

const OUTLIER_RATIO = 3
const BREAK_START_HEADROOM = 1.25
const BREAK_END_SHARE = 0.9

const outlierBreaks = computed(() => {
  if (!props.capOutliers) return undefined
  const [highest, nextHighest] = props.values.filter((value) => value !== null).sort((a, b) => b - a)
  if (!nextHighest || highest <= nextHighest * OUTLIER_RATIO) return undefined
  return [{ start: nextHighest * BREAK_START_HEADROOM, end: highest * BREAK_END_SHARE }]
})

const option = computed<OmrEChartsOption>(() => {
  const format = props.formatValue ?? makeCompact(props.locale)

  return {
    tooltip: { trigger: 'axis', valueFormatter: (value) => (value == null ? '–' : format(Number(value))) },
    grid: { top: 32, left: 8, right: 16, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: props.categories, axisLabel: { interval: 0 } },
    yAxis: {
      type: 'value',
      splitNumber: 5,
      splitLine: { show: props.gridLines },
      axisLabel: { formatter: (value: number | string) => format(Number(value)) },
      breaks: outlierBreaks.value,
    },
    series: [
      {
        type: 'bar',
        barWidth: '60%',
        data: props.values.map((value, index) => ({
          value,
          itemStyle: { color: index === props.highlightIndex ? (props.highlightColor ?? props.color) : props.color },
        })),
        itemStyle: { borderRadius: [3, 3, 0, 0] },
        label: {
          show: true,
          position: 'top',
          fontWeight: 'bold',
          formatter: (params) => (params.value == null ? '' : format(Number(params.value))),
        },
        ...(props.marker != null && {
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', color: props.markerColor, width: 1.5 },
            label: { show: !!props.markerLabel, formatter: () => props.markerLabel ?? '', position: 'insideEndTop' },
            data: [{ yAxis: props.marker }],
          },
        }),
      },
    ],
  }
})
</script>
